import type { DiskLocation } from '../Contracts/DiskNode'

/**
 * Адреси екрана диска (`DISK-001` §6):
 *
 * - `/disk` — перенаправляє на «Мій диск»;
 * - `/disk/recent` — нещодавні;
 * - `/disk/shared` — «Спільні зі мною», `/disk/shared/<папка>` — папка
 *   всередині спільної (без простору: його в людини може й не бути);
 * - `/disk/<простір>` — корінь простору, `/disk/<простір>/<папка>` — папка;
 * - `/disk/<простір>/trash` — кошик простору;
 * - `?file=<uuid>` — файл, відкритий у переглядачі (`FileViewerStore`);
 * - `/disk/node/<uuid>` — посилання на вузол без простору (сповіщення «з вами
 *   поділились», «Копіювати посилання»): `NodeLinkPage` відкриває папку чи файл
 *   у його папці — у просторі, якщо він у людини є, інакше в «Спільних зі мною».
 */
export const DISK_BASE = '/disk'
export const DISK_RECENT = 'recent'
export const DISK_SHARED = 'shared'
export const DISK_TRASH = 'trash'
export const DISK_FILE_PARAM = 'file'

export const diskFolderUrl = (storageUuid: string, folderUuid?: string | null): string =>
    folderUuid ? `${DISK_BASE}/${storageUuid}/${folderUuid}` : `${DISK_BASE}/${storageUuid}`

export const diskTrashUrl = (storageUuid: string): string => `${DISK_BASE}/${storageUuid}/${DISK_TRASH}`

export const diskRecentUrl = (): string => `${DISK_BASE}/${DISK_RECENT}`

export const diskSharedUrl = (folderUuid?: string | null): string =>
    folderUuid ? `${DISK_BASE}/${DISK_SHARED}/${folderUuid}` : `${DISK_BASE}/${DISK_SHARED}`

/** Посилання на вузол за uuid — `entityCode` сповіщень диска `Disk.Node`. */
export const DISK_NODE_ENTITY_CODE = 'Disk.Node'

export const diskNodeUrl = (uuid: string): string => `${DISK_BASE}/node/${encodeURIComponent(uuid)}`

/** Повна адреса вузла для «Копіювати посилання»: відкриють лише ті, хто має доступ. */
export const diskNodeAbsoluteUrl = (uuid: string): string => `${window.location.origin}${diskNodeUrl(uuid)}`

export const diskLocationUrl = (location: DiskLocation): string => {
    if (location.kind === 'recent') return diskRecentUrl()
    if (location.kind === 'trash') return diskTrashUrl(location.storageUuid)
    if (location.kind === 'shared') return diskSharedUrl(location.folderUuid)

    return diskFolderUrl(location.storageUuid, location.folderUuid)
}

/** Параметри маршруту `/disk/:storage/:folder?` → що відкрити. */
export const parseDiskLocation = (storage: string | undefined, folder: string | undefined): DiskLocation | null => {
    if (!storage) return null
    if (storage === DISK_RECENT) return { kind: 'recent' }
    if (storage === DISK_SHARED) return { kind: 'shared', folderUuid: folder ?? null }
    if (folder === DISK_TRASH) return { kind: 'trash', storageUuid: storage }

    return { kind: 'folder', storageUuid: storage, folderUuid: folder ?? null }
}
